/**
 * SyPI summary / preview flows: reset previews, schema-default resets,
 * summary form requirements, general preview builder, and the
 * schedule-with-schema-defaults handler.
 *
 * Adopts `runAsyncAction` in `saveScheduleWithSchemaDefaults` (simple
 * spinner wrap with a clean error surface).
 */

import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import { diff } from "deep-object-diff";
import { mergeAdvanced } from "object-merge-advanced";
import { getStore } from "@store";
import sypiAdapter from "@features/sypi/adapters";
import {
  isEmptyObject,
  removeEmptyValues,
  stringifyNestedKeyPaths,
} from "@utils/object-helpers";
import { toSentenceCase } from "@utils/string-helpers";
import {
  generateBlankConfig,
  mergeSypiVersionChanges,
  currentKeysOccurrenceChangesReport,
} from "@features/sypi/_utils/sypi-interface-helpers";
import { CONFIG_SCHEDULER, SYPI_S3 } from "@actions/types";
import { STATE_CONTANTS } from "@constants";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  showSpinner,
  stopSpinner,
  clickTab,
  setAlertDock,
  clearErrors,
  clearChanges,
  clearLog,
  logChangedKey,
  recordChangeObject,
} from "@actions/global";
import {
  setSypiConfigStatus,
  setSummaryViewType,
  setSypiSectionSummaryName,
  toggleSummaryModal,
  toggleSummaryConfirm,
  loadImportedInputs,
  loadSummaryChangesReview,
  loadGeneralSummaryPreview,
  loadNewDefaultsPreview,
  mergeDefaultConfig,
  addSchemaDefaultsToChanges,
  loadScheduledDraft,
  loadInitialScheduledDraft,
} from "@features/sypi/actions";
import { getSummaryPreview } from "./sypi-shared";
import {
  fetchSypiSchema,
  fetchSypiConfig,
  mergeAndHandleVisibilitySettings,
} from "./sypi-config";
import { dispatchErrors } from "./sypi-errors";
import { saveScheduledDraftedConfig } from "./sypi-schedule";
import { loadScheduledDraftConfig } from "./sypi-history";

export const fetchResetPreview = async (
  currentConfig,
  defaultConfig,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  store.dispatch(
    clickTab(STATE_CONTANTS.DISPLAY_RESET_SUMMARY, CONFIG_SCHEDULER)
  );
  const diffConfig = isEmptyObject(defaultConfig)
    ? {}
    : removeEmptyValues(diff(currentConfig, defaultConfig));
  getSummaryPreview(currentConfig, diffConfig, defaultConfig, undefined);
  store.dispatch(
    setSypiSectionSummaryName(STATE_CONTANTS.DISPLAY_RESET_SUMMARY)
  );
  store.dispatch(toggleSummaryModal(true));
  store.dispatch(stopSpinner());
};

export const handleResetToSchemaDefaults = (
  currentConfig,
  defaultConfig,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const { clientSchema } = store.getState().sypiSchema;
  const { generalPreview } = store.getState().sypiConfigScheduler;
  const { summaryView } = generalPreview || [];
  let mergedConfig = mergeAdvanced(currentConfig, defaultConfig, {
    concatInsteadOfMerging: false,
  });
  if (summaryView.length > 0) {
    let updatedImports = {};
    summaryView.forEach((key) => {
      updatedImports = { ...updatedImports, [key.keyName]: key.newKeyValue };
    });
    store.dispatch(loadImportedInputs(updatedImports));
    store.dispatch(mergeDefaultConfig(mergedConfig));
    dispatchErrors(mergedConfig, clientSchema);
    store.dispatch(
      setAlertDock({
        title: "Client Config Reset to Schema Defaults",
        message:
          "Schema default values have been loaded. Please schedule your changes to reset client config values to schema default values.",
      })
    );
  }
  store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  store.dispatch(stopSpinner());
};

/**
 * Loads either the scheduled/draft list or the completed-history list,
 * routes it into the correct summary tab, and computes multi-scheduler
 * error keys for the scheduled path.
 */
export const setSummaryFormRequirements = async (
  viewType,
  updateAction,
  release_version,
  store = getStore()
) => {
  const storeContents = store.getState();
  let finalReport = null;
  const { sypiSchedulerConfig, sypiNewView, sypiConfigScheduler, sypiSchema } =
    storeContents;
  const {
    scheduleDraftSet,
    sypiConfigSaveState,
    historySet,
    configSchedulerStatus,
    initialScheduleDraft,
  } = sypiConfigScheduler;
  store.dispatch(showSpinner());
  const isScheduledDraft = configSchedulerStatus === "scheduledDraft";
  const status =
    updateAction === "scheduledSummary"
      ? "scheduled"
      : updateAction === "draftSummary"
      ? "draft"
      : "completed";
  const { apiError, configSchedules } = isScheduledDraft
    ? scheduleDraftSet && scheduleDraftSet.length
      ? { configSchedules: scheduleDraftSet }
      : await sypiAdapter.fetchScheduledDraftList()
    : historySet && historySet.length
    ? { configSchedules: historySet }
    : await sypiAdapter.fetchHistoryConfig();

  if (apiError && apiError.details) {
    finalReport = {
      type: "ALERT_DOCK",
      title: `Unable to load config. `,
      message:
        "Please contact the application development team for assistance.",
    };
  } else {
    store.dispatch(setSummaryViewType(viewType));
    let keyReport = {};
    if (status == "scheduled") {
      const scheduledChanges =
        sypiConfigSaveState === "edit"
          ? configSchedules?.filter(
              (configSchedule) =>
                configSchedule.status === "scheduled" &&
                configSchedule.release_version ===
                  sypiSchema.activeReleaseVersion &&
                configSchedule.liveScheduleDate ===
                  initialScheduleDraft.liveScheduleDate
            )
          : configSchedules?.filter(
              (configSchedule) =>
                configSchedule.status === "scheduled" &&
                configSchedule.release_version ===
                  sypiSchema.activeReleaseVersion
            );
      let { scheduledChangesWithCommonKeys, successFailedKeysReport } =
        currentKeysOccurrenceChangesReport(
          sypiNewView.changeDict,
          scheduledChanges
        );
      if (!isEmptyObject(scheduledChangesWithCommonKeys)) {
        store.dispatch(
          clickTab(STATE_CONTANTS.GENERAL_SCHEDULER_ERROR, CONFIG_SCHEDULER)
        );
        setGeneralPreviewSummary(
          SYPI_S3,
          scheduledChangesWithCommonKeys,
          initialScheduleDraft,
          successFailedKeysReport
        );
        store.dispatch(toggleSummaryConfirm(true));
        keyReport = scheduledChangesWithCommonKeys;
        finalReport = {
          type: "TOAST_MESSAGE",
          message: `Unable to modify keys`,
        };
      }
      store.dispatch(toggleSummaryModal(true));
      finalReport =
        viewType === "summary"
          ? {
              type: "TOAST_MESSAGE",
              message: `Summary of changes is ready to review`,
            }
          : null;
    }
    if (status === "completed") {
      store.dispatch(toggleSummaryConfirm(true));
    } else if (isEmptyObject(keyReport)) {
      await loadScheduledDraftConfig();
      store.dispatch(toggleSummaryConfirm(false));
      store.dispatch(setSypiConfigStatus(status));
    }
    finalReport =
      viewType === "summary"
        ? {
            type: "TOAST_MESSAGE",
            message: `Summary of changes is ready to review`,
          }
        : null;
    store.dispatch(toggleSummaryModal(true));
  }
  store.dispatch(stopSpinner());

  return finalReport;
};

export const setGeneralPreviewSummary = async (
  feature,
  changeDict,
  initialConfig,
  excludedKeys = {},
  scheduleNewDefaults = false,
  release_version,
  store = getStore()
) => {
  store.dispatch(showSpinner());

  let summaryReviewBundle = [
    ...(changeDict && !isEmptyObject(changeDict)
      ? mergeSypiVersionChanges(
          changeDict,
          feature,
          initialConfig,
          excludedKeys
        )
      : []),
  ];
  scheduleNewDefaults
    ? store.dispatch(
        loadNewDefaultsPreview(release_version, summaryReviewBundle)
      )
    : store.dispatch(loadGeneralSummaryPreview(summaryReviewBundle));
  store.dispatch(stopSpinner());
};

/**
 * Merges the master-schema defaults into the client config (if the schema
 * changed), then delegates to `saveScheduledDraftedConfig`. Uses
 * `runAsyncAction` for the spinner + centralized error surface.
 */
export const saveScheduleWithSchemaDefaults = async (
  store = getStore()
) => {
  return await runAsyncAction(
    store,
    async () => {
      const { changeDict } = store.getState().sypiNewView;
      const { activeReleaseVersion } = store.getState().sypiSchema;
      const sypiSchema = await fetchSypiSchema(activeReleaseVersion);
      const { formattedSchema, formattedMasterSchema } = sypiSchema;
      const schemasDifference = diff(formattedSchema, formattedMasterSchema);

      const sypiClientConfig = store.getState().sypiNewView.sypiConfig;
      const clientConfigTemplate = generateBlankConfig(formattedSchema);

      const {
        fullErrorReport: { schemaErrors },
      } = dispatchErrors(sypiClientConfig, formattedMasterSchema, false);

      if (
        (schemasDifference && !isEmptyObject(schemasDifference)) ||
        (schemaErrors && schemaErrors?.length)
      ) {
        let masterConfigTemplate = generateBlankConfig(formattedMasterSchema);

        const savedConfigChanges = diff(clientConfigTemplate, sypiClientConfig);

        const pathConfigChanges =
          savedConfigChanges && !isEmptyObject(savedConfigChanges)
            ? stringifyNestedKeyPaths(savedConfigChanges)
            : [];

        let updatedSypiConfig =
          pathConfigChanges && pathConfigChanges.length
            ? pathConfigChanges.reduce((accSypiConfig, path) => {
                if (has(accSypiConfig, path) && has(sypiClientConfig, path)) {
                  const savedValue = get(sypiClientConfig, path);
                  set(accSypiConfig, path, savedValue);
                }
                return accSypiConfig;
              }, masterConfigTemplate)
            : masterConfigTemplate;

        const mergeUpdatedConfig = mergeAndHandleVisibilitySettings(
          masterConfigTemplate,
          updatedSypiConfig,
          false
        );
        store.dispatch(addSchemaDefaultsToChanges(mergeUpdatedConfig));
        dispatchErrors(mergeUpdatedConfig, formattedMasterSchema);
      }
      return await saveScheduledDraftedConfig(changeDict);
    },
    { errorAction: "save", errorSubject: "schedule with schema defaults" }
  ).then((r) => (r.ok ? r.value : null));
};
